/**
 * CertificatePreview - Renders a live SVG certificate preview based on template config.
 * Supports 10 border styles, optional background image, flag, seal, corner decorations.
 */
import { forwardRef, useEffect, useState } from 'react'
import QRCode from 'qrcode'
import type { TemplateConfig } from './templatePresets'

interface Props {
  config: TemplateConfig
  studentName?: string
  backgroundImageUrl?: string | null
  verificationCode?: string | null
  verifyBaseUrl?: string
}

function BorderDecorations({ config }: { config: TemplateConfig }) {
  const { primaryColor, secondaryColor, borderStyle } = config

  switch (borderStyle) {
    case 'classic':
      return (
        <>
          <rect x="15" y="15" width="770" height="536" fill="none" stroke={primaryColor} strokeWidth="3" rx="4" />
          <rect x="22" y="22" width="756" height="522" fill="none" stroke={primaryColor} strokeWidth="1" rx="2" opacity="0.4" />
        </>
      )
    case 'modern':
      return (
        <>
          <rect x="20" y="20" width="760" height="526" fill="none" stroke={primaryColor} strokeWidth="4" rx="12" />
        </>
      )
    case 'double':
      return (
        <>
          <rect x="12" y="12" width="776" height="542" fill="none" stroke={primaryColor} strokeWidth="2" rx="2" />
          <rect x="20" y="20" width="760" height="526" fill="none" stroke={primaryColor} strokeWidth="2" rx="2" />
          <rect x="28" y="28" width="744" height="510" fill="none" stroke={primaryColor} strokeWidth="0.5" rx="2" opacity="0.3" />
        </>
      )
    case 'ornate':
      return (
        <>
          <rect x="15" y="15" width="770" height="536" fill="none" stroke={primaryColor} strokeWidth="3" rx="4" />
          <rect x="22" y="22" width="756" height="522" fill="none" stroke={primaryColor} strokeWidth="1" rx="2" opacity="0.4" />
          {[[30, 30, ''], [770, 30, 'scale(-1,1)'], [30, 536, 'scale(1,-1)'], [770, 536, 'scale(-1,-1)']].map(([x, y, s], i) => (
            <g key={i} transform={`translate(${x}, ${y}) ${s}`}>
              <path d="M0,40 Q0,0 40,0" fill="none" stroke={primaryColor} strokeWidth="2" />
              <path d="M0,30 Q0,5 30,0" fill="none" stroke={primaryColor} strokeWidth="1" opacity="0.5" />
              <circle cx="5" cy="5" r="4" fill={primaryColor} opacity="0.3" />
            </g>
          ))}
        </>
      )
    case 'minimal':
      return (
        <rect x="30" y="30" width="740" height="506" fill="none" stroke={primaryColor} strokeWidth="0.5" rx="0" opacity="0.4" />
      )
    case 'thick':
      return (
        <>
          <rect x="10" y="10" width="780" height="546" fill="none" stroke={primaryColor} strokeWidth="8" rx="4" />
          <rect x="20" y="20" width="760" height="526" fill="none" stroke={secondaryColor} strokeWidth="2" rx="2" />
        </>
      )
    case 'elegant':
      return (
        <>
          <rect x="18" y="18" width="764" height="530" fill="none" stroke={primaryColor} strokeWidth="1.5" rx="2" />
          {[200, 400, 600].map((x) => (
            <g key={`top-${x}`}>
              <polygon points={`${x},14 ${x + 5},18 ${x},22 ${x - 5},18`} fill={primaryColor} opacity="0.5" />
              <polygon points={`${x},544 ${x + 5},548 ${x},552 ${x - 5},548`} fill={primaryColor} opacity="0.5" />
            </g>
          ))}
          {[180, 380].map((y) => (
            <g key={`side-${y}`}>
              <polygon points={`14,${y} 18,${y + 5} 22,${y} 18,${y - 5}`} fill={primaryColor} opacity="0.5" />
              <polygon points={`778,${y} 782,${y + 5} 786,${y} 782,${y - 5}`} fill={primaryColor} opacity="0.5" />
            </g>
          ))}
        </>
      )
    case 'art-deco':
      return (
        <>
          <rect x="15" y="15" width="770" height="536" fill="none" stroke={primaryColor} strokeWidth="3" rx="0" />
          {[[15, 15], [755, 15], [15, 521], [755, 521]].map(([x, y], i) => (
            <g key={i}>
              <rect x={x} y={y} width="30" height="30" fill="none" stroke={secondaryColor} strokeWidth="1.5" />
              <rect x={x + 5} y={y + 5} width="20" height="20" fill="none" stroke={secondaryColor} strokeWidth="1" opacity="0.5" />
              <rect x={x + 10} y={y + 10} width="10" height="10" fill={secondaryColor} opacity="0.15" />
            </g>
          ))}
          <line x1="50" y1="18" x2="750" y2="18" stroke={secondaryColor} strokeWidth="1" opacity="0.3" />
          <line x1="50" y1="548" x2="750" y2="548" stroke={secondaryColor} strokeWidth="1" opacity="0.3" />
        </>
      )
    case 'ribbon':
      return (
        <>
          <rect x="20" y="20" width="760" height="526" fill="none" stroke={primaryColor} strokeWidth="2" rx="4" />
          <rect x="100" y="10" width="600" height="20" fill={primaryColor} opacity="0.1" rx="3" />
          <rect x="150" y="13" width="500" height="14" fill={primaryColor} opacity="0.08" rx="2" />
          <rect x="100" y="536" width="600" height="20" fill={primaryColor} opacity="0.1" rx="3" />
          <rect x="150" y="539" width="500" height="14" fill={primaryColor} opacity="0.08" rx="2" />
        </>
      )
    case 'none':
    default:
      return null
  }
}

function CornerDecorations({ config }: { config: TemplateConfig }) {
  if (!config.showCornerDecorations) return null
  const c = config.primaryColor

  return (
    <>
      {[
        { x: 30, y: 30, sx: 1, sy: 1 },
        { x: 770, y: 30, sx: -1, sy: 1 },
        { x: 30, y: 536, sx: 1, sy: -1 },
        { x: 770, y: 536, sx: -1, sy: -1 },
      ].map((p, i) => (
        <g key={i} transform={`translate(${p.x}, ${p.y}) scale(${p.sx}, ${p.sy})`}>
          <path d="M0,30 L0,5 Q0,0 5,0 L30,0" fill="none" stroke={c} strokeWidth="2" />
          <circle cx="5" cy="5" r="3" fill={c} />
        </g>
      ))}
    </>
  )
}

function FlagDecoration({ show }: { show: boolean }) {
  if (!show) return null
  return (
    <>
      <rect x="350" y="50" width="10" height="30" fill="#0055A4" rx="2" />
      <rect x="362" y="50" width="10" height="30" fill="#FFFFFF" stroke="#e5e7eb" strokeWidth="0.5" rx="2" />
      <rect x="374" y="50" width="10" height="30" fill="#EF4135" rx="2" />
    </>
  )
}

const CertificatePreview = forwardRef<SVGSVGElement, Props>(
  ({ config, studentName = 'Nombre del Estudiante', backgroundImageUrl, verificationCode, verifyBaseUrl }, ref) => {
    const {
      primaryColor,
      backgroundColor,
      textColor,
      headerText,
      titleText,
      subtitleText,
      achievementPlaceholder,
      footerText,
      fontFamily,
      showFlag,
    } = config

    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null)

    const verifyUrl = verificationCode && verifyBaseUrl
      ? `${verifyBaseUrl}/verify/${verificationCode}`
      : null

    useEffect(() => {
      if (!verifyUrl) {
        QRCode.toDataURL('https://nomadlexis.com/verify/EJEMPLO', {
          width: 120, margin: 1,
          color: { dark: '#374151', light: '#ffffff' },
        }).then(setQrDataUrl).catch(() => {})
        return
      }
      QRCode.toDataURL(verifyUrl, {
        width: 120, margin: 1,
        color: { dark: '#374151', light: '#ffffff' },
      }).then(setQrDataUrl).catch(() => {})
    }, [verifyUrl])

    const today = new Intl.DateTimeFormat('es-ES', {
      day: 'numeric', month: 'long', year: 'numeric',
    }).format(new Date())

    // ✅ Posiciones precalculadas — elimina todos los ternarios showFlag
    const pos = {
      header:      showFlag ? 120 : 100,
      title:       showFlag ? 168 : 148,
      line:        showFlag ? 185 : 165,
      subtitle:    showFlag ? 215 : 200,
      name:        showFlag ? 268 : 255,
      nameUnder:   showFlag ? 282 : 269,
      achievement: showFlag ? 320 : 310,
      achText:     showFlag ? 355 : 345,
      footer:      showFlag ? 390 : 380,
      divider:     showFlag ? 420 : 410,
      date:        showFlag ? 455 : 445,
    }

    // ✅ Posición del sello precalculada
    const sealX = qrDataUrl ? 340 : 400

    return (
      <svg
        ref={ref}
        width="800"
        height="566"
        viewBox="0 0 800 566"
        xmlns="http://www.w3.org/2000/svg"
        style={{ background: backgroundColor }}
      >
        <rect width="800" height="566" fill={backgroundColor} />

        {backgroundImageUrl && (
          <image href={backgroundImageUrl} x="0" y="0" width="800" height="566" preserveAspectRatio="xMidYMid slice" />
        )}

        <BorderDecorations config={config} />
        {!backgroundImageUrl && <CornerDecorations config={config} />}
        <FlagDecoration show={showFlag} />

        <text x="400" y={pos.header} textAnchor="middle" fontSize="14" fill="#6b7280" fontFamily={fontFamily} letterSpacing="4">
          {headerText}
        </text>

        <text x="400" y={pos.title} textAnchor="middle" fontSize="32" fill={primaryColor} fontFamily={fontFamily} fontWeight="600">
          {titleText}
        </text>

        <line x1="250" y1={pos.line} x2="550" y2={pos.line} stroke={primaryColor} strokeWidth="1" opacity="0.4" />
        <circle cx="400" cy={pos.line} r="3" fill={primaryColor} opacity="0.4" />

        <text x="400" y={pos.subtitle} textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily={fontFamily}>
          {subtitleText}
        </text>

        <text x="400" y={pos.name} textAnchor="middle" fontSize="36" fill={textColor} fontFamily={fontFamily} fontStyle="italic" fontWeight="700">
          {studentName}
        </text>

        <line x1="200" y1={pos.nameUnder} x2="600" y2={pos.nameUnder} stroke="#d1d5db" strokeWidth="1" />

        <text x="400" y={pos.achievement} textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily={fontFamily}>
          por haber completado exitosamente
        </text>

        <text x="400" y={pos.achText} textAnchor="middle" fontSize="22" fill={primaryColor} fontFamily={fontFamily} fontWeight="600">
          {achievementPlaceholder}
        </text>

        <text x="400" y={pos.footer} textAnchor="middle" fontSize="13" fill="#6b7280" fontFamily={fontFamily}>
          {footerText}
        </text>

        <line x1="300" y1={pos.divider} x2="500" y2={pos.divider} stroke={primaryColor} strokeWidth="0.5" opacity="0.3" />

        <text x="400" y={pos.date} textAnchor="middle" fontSize="12" fill="#9ca3af" fontFamily={fontFamily}>
          {today}
        </text>

        {config.showSeal && (
          <>
            <circle cx={sealX} cy="498" r="25" fill="none" stroke={config.accentColor || primaryColor} strokeWidth="1.5" opacity="0.3" />
            <circle cx={sealX} cy="498" r="20" fill="none" stroke={config.accentColor || primaryColor} strokeWidth="0.5" opacity="0.2" />
            <polygon
              points={`${sealX},480 ${sealX+3},491 ${sealX+14},491 ${sealX+5},498 ${sealX+8},509 ${sealX},503 ${sealX-8},509 ${sealX-5},498 ${sealX-14},491 ${sealX-3},491`}
              fill={config.accentColor || primaryColor}
              opacity="0.15"
            />
            <text x={sealX} y="502" textAnchor="middle" fontSize="8" fill={config.accentColor || primaryColor} opacity="0.4" fontFamily="Georgia, serif">
              A1
            </text>
          </>
        )}

        {qrDataUrl && (
          <>
            <image href={qrDataUrl} x="700" y="476" width="70" height="70" opacity="0.85" />
            <text x="735" y="556" textAnchor="middle" fontSize="6" fill="#9ca3af" fontFamily={fontFamily}>
              Verificar
            </text>
          </>
        )}

        {verificationCode && (
          <text x="40" y="550" fontSize="7" fill="#9ca3af" fontFamily="monospace" opacity="0.6">
            ID: {verificationCode}
          </text>
        )}
      </svg>
    )
  }
)

CertificatePreview.displayName = 'CertificatePreview'
export default CertificatePreview